import React from "react";
import { RadioOption } from "../../atoms/RadioOption";
import { Dropdown } from "../Dropdown";
import { InlinePickDropdown } from "../InlinePickDropdown";
import type { DistributionMenuBinding } from "../DistributionQuarterlyFields/DistributionQuarterlyFields.types";
import { Field, FieldLabel } from "../shared/scheduleField.styles";
import {
  BUSINESS_NTH_OPTIONS,
  MONTH_DAY_OPTIONS,
  NTH_OPTIONS,
  RULE_MODE_OPTIONS,
  WEEKDAY_OPTIONS,
  parseBusinessNth,
  parseMonthDay,
  parseNth,
  parseWeekday,
} from "./DistributionMonthlyRuleFields.options";
import { Control, Controls, RadioSlot, Row, Rows } from "./DistributionMonthlyRuleFields.styles";
import type { DistributionMonthlyRuleFieldsProps, DistributionMonthlyRuleMode } from "./DistributionMonthlyRuleFields.types";

interface RuleSelectProps {
  value: string;
  options: { value: string; label: string }[];
  menu: DistributionMenuBinding;
  ariaLabel: string;
  scroll?: boolean;
  /** Set inside a sentence: a content-sized trigger, no flex column. */
  inline?: boolean;
  onPick: (value: string) => void;
}

/** One select of a row, bound to a menu the organism owns. */
const RuleSelect: React.FC<RuleSelectProps> = ({ value, options, menu, ariaLabel, scroll = false, inline = false, onPick }) => {
  const select = (picked: string) => {
    onPick(picked);
    menu.onClose();
  };
  if (inline) {
    return (
      <InlinePickDropdown
        value={value}
        options={options}
        open={menu.open}
        triggerRef={menu.triggerRef}
        menuRef={menu.menuRef}
        menuRect={menu.menuRect}
        showScrollbar={scroll}
        ariaLabel={ariaLabel}
        onToggle={menu.onToggle}
        onSelect={select}
      />
    );
  }
  return (
    <Control>
      <Dropdown
        value={value}
        options={options}
        open={menu.open}
        triggerRef={menu.triggerRef}
        menuRef={menu.menuRef}
        menuRect={menu.menuRect}
        size="md"
        compact
        fullWidth
        showScrollbar={scroll}
        ariaLabel={ariaLabel}
        onToggle={menu.onToggle}
        onSelect={select}
      />
    </Control>
  );
};

/**
 * Monthly and quarterly "On" (prototype `repeatHTML` month branch): a day of the month, the
 * first … last weekday, or the first or last business day. Picking in a row selects that row.
 */
export const DistributionMonthlyRuleFields: React.FC<DistributionMonthlyRuleFieldsProps> = ({
  id,
  label,
  value,
  menus,
  onChange,
  appearance = "field",
  modeMenu,
  className,
}) => {
  const radio = (mode: DistributionMonthlyRuleMode, text: string) => (
    <RadioSlot>
      <RadioOption name={`${id}-mode`} value={mode} checked={value.mode === mode} label={text} onChange={() => onChange({ mode })} />
    </RadioSlot>
  );
  if (appearance === "inline") {
    return (
      <>
        {modeMenu ? (
          <RuleSelect
            inline
            value={value.mode}
            options={RULE_MODE_OPTIONS}
            menu={modeMenu}
            ariaLabel="Day rule"
            onPick={(picked) => onChange({ mode: picked === "weekday" ? "weekday" : picked === "business_day" ? "business_day" : "day" })}
          />
        ) : null}
        {value.mode === "day" ? (
          <RuleSelect inline value={String(value.day)} options={MONTH_DAY_OPTIONS} menu={menus.day} ariaLabel="Day of the month" scroll onPick={(picked) => onChange({ mode: "day", day: parseMonthDay(picked) })} />
        ) : null}
        {value.mode === "weekday" ? (
          <>
            <RuleSelect inline value={String(value.nth)} options={NTH_OPTIONS} menu={menus.nth} ariaLabel="Which weekday of the month" onPick={(picked) => onChange({ mode: "weekday", nth: parseNth(picked) })} />
            <RuleSelect inline value={String(value.weekday)} options={WEEKDAY_OPTIONS} menu={menus.weekday} ariaLabel="Day of the week" onPick={(picked) => onChange({ mode: "weekday", weekday: parseWeekday(picked) })} />
          </>
        ) : null}
        {value.mode === "business_day" ? (
          <RuleSelect inline value={String(value.businessNth)} options={BUSINESS_NTH_OPTIONS} menu={menus.business} ariaLabel="Which business day of the month" onPick={(picked) => onChange({ mode: "business_day", businessNth: parseBusinessNth(picked) })} />
        ) : null}
      </>
    );
  }
  return (
    <Field className={className}>
      <FieldLabel id={`${id}-label`}>{label}</FieldLabel>
      <Rows role="radiogroup" aria-labelledby={`${id}-label`}>
        <Row>
          {radio("day", "Day")}
          <Controls>
            <RuleSelect
              value={String(value.day)}
              options={MONTH_DAY_OPTIONS}
              menu={menus.day}
              ariaLabel="Day of the month"
              scroll
              onPick={(picked) => onChange({ mode: "day", day: parseMonthDay(picked) })}
            />
          </Controls>
        </Row>
        <Row>
          {radio("weekday", "Weekday")}
          <Controls>
            <RuleSelect
              value={String(value.nth)}
              options={NTH_OPTIONS}
              menu={menus.nth}
              ariaLabel="Which weekday of the month"
              onPick={(picked) => onChange({ mode: "weekday", nth: parseNth(picked) })}
            />
            <RuleSelect
              value={String(value.weekday)}
              options={WEEKDAY_OPTIONS}
              menu={menus.weekday}
              ariaLabel="Day of the week"
              onPick={(picked) => onChange({ mode: "weekday", weekday: parseWeekday(picked) })}
            />
          </Controls>
        </Row>
        <Row>
          {radio("business_day", "Business day")}
          <Controls>
            <RuleSelect
              value={String(value.businessNth)}
              options={BUSINESS_NTH_OPTIONS}
              menu={menus.business}
              ariaLabel="Which business day of the month"
              onPick={(picked) => onChange({ mode: "business_day", businessNth: parseBusinessNth(picked) })}
            />
          </Controls>
        </Row>
      </Rows>
    </Field>
  );
};

export default DistributionMonthlyRuleFields;
